import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { apiRequest } from '../../utils/api';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const createDefaultFilters = () => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  return {
    startMonth: String(previous.getMonth() + 1),
    startYear: String(previous.getFullYear()),
    endMonth: String(currentMonth),
    endYear: String(currentYear),
    status: 'all',
    search: '',
    employeeId: '',
  };
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}-${month}-${year}`;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString('en-GB');
};

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const formatAmount = (value) => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value;
  }

  return numeric.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const resolveMonthLabel = (record) => {
  if (record?.monthLabel) {
    return record.monthLabel;
  }

  if (!record?.paymentDate) {
    return '-';
  }

  const parsed = new Date(record.paymentDate);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

const getStatusVariant = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'sent':
      return 'success';
    case 'failed':
      return 'danger';
    default:
      return 'secondary';
  }
};

const getStatusLabel = (status) => {
  if (!status) {
    return 'Pending';
  }

  const normalized = status.toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const SendPaySlip = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(() => createDefaultFilters());
  const [searchInput, setSearchInput] = useState('');
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState(null);
  const [processingIds, setProcessingIds] = useState([]);
  const [processingAll, setProcessingAll] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [meta, setMeta] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });

  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  const monthOptions = useMemo(
    () =>
      MONTH_NAMES.map((month, index) => ({
        value: String(index + 1),
        label: month,
      })),
    [],
  );

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 6; i += 1) {
      years.push(String(currentYear - i));
    }
    return years;
  }, []);

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'All statuses' },
      { value: 'pending', label: 'Pending' },
      { value: 'sent', label: 'Sent' },
      { value: 'failed', label: 'Failed' },
    ],
    [],
  );

  const loadStatuses = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      params.set('sortBy', 'paymentDate');
      params.set('sortDir', 'DESC');

      if (filters.search) {
        params.set('search', filters.search.trim());
      }
      if (filters.startMonth) {
        params.set('startMonth', filters.startMonth);
      }
      if (filters.startYear) {
        params.set('startYear', filters.startYear);
      }
      if (filters.endMonth) {
        params.set('endMonth', filters.endMonth);
      }
      if (filters.endYear) {
        params.set('endYear', filters.endYear);
      }
      if (filters.status && filters.status !== 'all') {
        params.set('status', filters.status);
      }
      if (filters.employeeId) {
        params.set('employeeId', filters.employeeId);
      }

      const { data, meta: responseMeta, message } = await apiRequest(
        `/api/admin/payslips/send-status?${params.toString()}`,
        {},
        navigate,
      );

      if (message) {
        console.debug(message);
      }

      const resolvedPage = responseMeta?.page ?? page;
      setRecords(Array.isArray(data) ? data : []);
      setMeta({
        page: resolvedPage,
        limit: responseMeta?.limit ?? limit,
        total: responseMeta?.total ?? (Array.isArray(data) ? data.length : 0),
        totalPages: responseMeta?.totalPages ?? 0,
      });
      if (resolvedPage !== page) {
        setPage(resolvedPage);
      }
    } catch (err) {
      console.error('Failed to load payslip email statuses', err);
      setError(err.message || 'Failed to load payslip email statuses.');
      setRecords([]);
      setMeta({ page: 1, limit, total: 0, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  }, [filters, limit, navigate, page]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  const loadEmployeeOptions = useCallback(async () => {
    try {
      const { data: employeeData } = await apiRequest('/api/admin/employees?status=active', {}, navigate);
      const employeeList = Array.isArray(employeeData?.employees)
        ? employeeData.employees
        : Array.isArray(employeeData)
          ? employeeData
          : [];
      setEmployees(employeeList);
    } catch (err) {
      console.error('Failed to load employee options for payslip sending', err);
    }
  }, [navigate]);

  useEffect(() => {
    loadEmployeeOptions();
  }, [loadEmployeeOptions]);

  const applySearch = useCallback(
    (event) => {
      event.preventDefault();
      setFilters((prev) => ({ ...prev, search: searchInput.trim() }));
      setPage(1);
    },
    [searchInput],
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    const defaults = createDefaultFilters();
    setFilters(defaults);
    setSearchInput(defaults.search);
    setPage(1);
  };

  const triggerSend = useCallback(
    async (payslipIds) => {
      if (!Array.isArray(payslipIds) || payslipIds.length === 0) {
        setStatusMessage({
          type: 'info',
          message: 'No payslips selected for sending.',
        });
        return;
      }

      setStatusMessage(null);

      try {
        const { message } = await apiRequest(
          '/api/admin/payslips/send',
          {
            method: 'POST',
            body: { payslipIds },
          },
          navigate,
        );

        setStatusMessage({
          type: 'success',
          message: message || 'Payslip emails were triggered successfully.',
        });
        await loadStatuses();
      } catch (err) {
        console.error('Failed to send payslip emails', err);
        setStatusMessage({
          type: 'error',
          message: err.message || 'Failed to send payslip emails.',
        });
        await loadStatuses();
      }
    },
    [loadStatuses, navigate],
  );

  const handleSendSingle = async (payslipId) => {
    if (!payslipId) {
      return;
    }

    setProcessingIds((prev) => [...prev, payslipId]);
    try {
      await triggerSend([payslipId]);
    } finally {
      setProcessingIds((prev) => prev.filter((id) => id !== payslipId));
    }
  };

  const handleSendAll = async () => {
    const targetIds = records.filter((record) => record && record.id).map((record) => record.id);
    if (targetIds.length === 0) {
      setStatusMessage({ type: 'info', message: 'There are no payslips to send for the selected filters.' });
      return;
    }

    setProcessingAll(true);
    try {
      await triggerSend(targetIds);
    } finally {
      setProcessingAll(false);
    }
  };

  const isProcessing = (payslipId) => processingIds.includes(payslipId);

  const totalPages = meta.totalPages || 0;

  return (
    <DashboardLayout>
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <h4 className="mb-0">Send Payslips</h4>
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/payslip')}>
            Back
          </button>
        </div>

        {statusMessage && (
          <div
            className={`alert alert-${
              statusMessage.type === 'error'
                ? 'danger'
                : statusMessage.type === 'info'
                ? 'info'
                : 'success'
            } alert-dismissible fade show`}
            role="alert"
          >
            {statusMessage.message}
            <button type="button" className="btn-close" aria-label="Close" onClick={() => setStatusMessage(null)} />
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <form className="row g-3 align-items-end" onSubmit={applySearch}>
              <div className="col-md-3">
                <label htmlFor="start-month" className="form-label">
                  From month
                </label>
                <select
                  id="start-month"
                  className="form-select"
                  value={filters.startMonth}
                  onChange={(event) => handleFilterChange('startMonth', event.target.value)}
                >
                  {monthOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label htmlFor="start-year" className="form-label">
                  From year
                </label>
                <select
                  id="start-year"
                  className="form-select"
                  value={filters.startYear}
                  onChange={(event) => handleFilterChange('startYear', event.target.value)}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label htmlFor="end-month" className="form-label">
                  To month
                </label>
                <select
                  id="end-month"
                  className="form-select"
                  value={filters.endMonth}
                  onChange={(event) => handleFilterChange('endMonth', event.target.value)}
                >
                  {monthOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label htmlFor="end-year" className="form-label">
                  To year
                </label>
                <select
                  id="end-year"
                  className="form-select"
                  value={filters.endYear}
                  onChange={(event) => handleFilterChange('endYear', event.target.value)}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label htmlFor="status-filter" className="form-label">
                  Status
                </label>
                <select
                  id="status-filter"
                  className="form-select"
                  value={filters.status}
                  onChange={(event) => handleFilterChange('status', event.target.value)}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label htmlFor="employee-search" className="form-label">
                  Search employee
                </label>
                <input
                  id="employee-search"
                  type="search"
                  className="form-control"
                  placeholder="Search by name, email, or employee ID"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </div>

              <div className="col-md-3">
                <label htmlFor="employee-filter" className="form-label">
                  Employee
                </label>
                <select
                  id="employee-filter"
                  className="form-select"
                  value={filters.employeeId}
                  onChange={(event) => handleFilterChange('employeeId', event.target.value)}
                >
                  <option value="">All employees</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.name ?? employee.employee_name ?? employee.showEmployeeId ?? employee.id}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-2 d-flex gap-2">
                <button type="submit" className="btn btn-primary w-100">
                  Apply
                </button>
                <button type="button" className="btn btn-outline-secondary w-100" onClick={resetFilters}>
                  Reset
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleSendAll}
              disabled={processingAll || loading || records.length === 0}
            >
              {processingAll ? 'Sending…' : 'Send all'}
            </button>
          </div>
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="rows-per-page" className="form-label mb-0">
              Rows per page
            </label>
            <select
              id="rows-per-page"
              className="form-select"
              style={{ width: '90px' }}
              value={limit}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setPage(1);
              }}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-4">Loading payslips…</div>
            ) : records.length === 0 ? (
              <div className="text-center py-4">No payslips found for the selected filters.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Email</th>
                      <th>Month</th>
                      <th>Payment date</th>
                      <th>Net pay</th>
                      <th>Status</th>
                      <th>Last update</th>
                      <th style={{ width: '110px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record) => (
                      <tr key={record.id}>
                        <td>
                          <div className="fw-semibold">{record.employeeName ?? '-'}</div>
                          <div className="text-muted small">{record.empCode ?? '-'}</div>
                        </td>
                        <td>{record.employeeEmail ?? '-'}</td>
                        <td>{resolveMonthLabel(record)}</td>
                        <td>{formatDate(record.paymentDate)}</td>
                        <td>{formatAmount(record.netPay)}</td>
                        <td>
                          <span className={`badge bg-${getStatusVariant(record.status)}`}>
                            {getStatusLabel(record.status)}
                          </span>
                          {record.errorMessage && (
                            <div className="text-danger small mt-1">{record.errorMessage}</div>
                          )}
                        </td>
                        <td>{formatDateTime(record.sentAt || record.updatedAt)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary w-100"
                            onClick={() => handleSendSingle(record.id)}
                            disabled={processingAll || isProcessing(record.id)}
                          >
                            {isProcessing(record.id) ? 'Sending…' : 'Send'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
          <div className="text-muted">
            Showing
            {' '}
            <strong>
              {records.length > 0 ? (meta.page - 1) * meta.limit + 1 : 0}
            </strong>
            {' '}
            to{' '}
            <strong>
              {records.length > 0
                ? Math.min(meta.page * meta.limit, meta.total)
                : 0}
            </strong>
            {' '}
            of{' '}
            <strong>{meta.total}</strong>
            {' '}
            payslips
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page <= 1 || loading}
            >
              Previous
            </button>
            <span className="align-self-center">
              Page {meta.page} of {totalPages || 1}
            </span>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                if (!totalPages || page < totalPages) {
                  setPage((prev) => prev + 1);
                }
              }}
              disabled={loading || (totalPages ? page >= totalPages : records.length < meta.limit)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SendPaySlip;
